import { useEffect, useState } from 'react';
import { Check, Loader2, MapPin, Trash2 } from 'lucide-react';
import type { plans } from '@/lib/client';
import { REVERT_STATUS_TEXT, STEP_RUN_STATUS_TEXT } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { usePlansStore } from '@/stores/plansStore';
import { takeMeThere } from '@/stores/teleport';
import { ChannelBadge, MovedBadge } from './badges';
import { Prose } from './Prose';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function Json({ value }: { value: unknown }) {
  return <pre className="max-h-48 overflow-auto rounded-md bg-background p-2 text-[11px] leading-snug text-foreground/90">{JSON.stringify(value, null, 2)}</pre>;
}

/** The request body as editable JSON: parsed on blur, kept as text while invalid. */
function BodyEditor({ body, onChange }: { body: Record<string, unknown> | undefined; onChange: (body: Record<string, unknown>) => void }) {
  const [text, setText] = useState(JSON.stringify(body ?? {}, null, 2));
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setText(JSON.stringify(body ?? {}, null, 2));
    setInvalid(false);
  }, [body]);
  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          try {
            const parsed: unknown = JSON.parse(text);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
            setInvalid(false);
            if (JSON.stringify(parsed) !== JSON.stringify(body ?? {})) onChange(parsed as Record<string, unknown>);
          } catch {
            setInvalid(true);
          }
        }}
        rows={Math.min(14, Math.max(4, text.split('\n').length))}
        spellCheck={false}
        className={cn('w-full rounded-md border bg-background p-2 font-mono text-[11px] leading-snug text-foreground', invalid ? 'border-red-500' : 'border-border')}
      />
      {invalid && <p className="mt-1 text-xs text-red-400">Not valid JSON; the previous body is kept.</p>}
    </div>
  );
}

/** How the step is undone, in plain words (D14), as one sentence. */
function revertLine(revert: plans.RevertSpec): string {
  switch (revert.kind) {
    case 'delete': return 'ZAC deletes what this step made.';
    case 'patch': return 'ZAC puts back the values it read just before the change.';
    case 'recipe': return 'ZAC runs the same recipe with the values it read before the change.';
    case 'agent': return 'ZAC undoes it in your browser, from what it did; you do it by hand if it cannot.';
    case 'manual': return revert.instructions ?? 'By hand.';
  }
}

/** Whether the step checked out, in plain words: what the read back after it found. */
function checkedLine(verification: plans.Verification): string {
  if (verification.skipped) return verification.skipped === 'agent' ? 'Checked on the page by the browser agent.' : `Not checked (${verification.skipped.replace(/_/g, ' ')}).`;
  const n = verification.checks.length;
  return verification.passed ? `Checked: Zoom shows ${n === 1 ? 'the value' : `all ${n} values`} as asked.` : 'Checked: Zoom does not show every value as asked.';
}

/** What the browser did for a step the API made: the scopes it added to the app, and a hand-over to the agent. */
function browserHelp(run: plans.StepRunView): string[] {
  const out: string[] = [];
  const scopes = run.response?.['scopes_added'];
  if (Array.isArray(scopes) && scopes.length > 0) out.push(`ZAC added ${scopes.length === 1 ? 'the scope' : 'the scopes'} ${scopes.join(', ')} to your Zoom app in the browser, then made the call again.`);
  const handover = run.response?.['api_handover'] as { handed_over?: unknown } | undefined;
  if (typeof handover?.handed_over === 'string') out.push(`The API could not do it (${handover.handed_over}), so ZAC did it in your browser.`);
  return out;
}

/** "Take me there" for a human step (D13): the walk is read only; its line shows under the button. */
function TakeMeThere({ plan, step, entryId }: { plan: plans.PlanView; step: plans.PlanStepView; entryId: string }) {
  const [walking, setWalking] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  const go = async () => {
    setWalking(true);
    setLine(null);
    try {
      setLine(await takeMeThere({ entryId, label: step.title, from: { planId: plan.id, stepKey: step.key } }));
    } finally {
      setWalking(false);
    }
  };
  return (
    <>
      <button onClick={() => void go()} disabled={walking} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-50">
        {walking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />} Take me there
      </button>
      {line && <p className="basis-full text-xs text-muted-foreground">{line}</p>}
    </>
  );
}

/** The selected step: what it is, what it needs, how to change it, and what its run did. */
export function StepPanel({ plan, step, editable }: { plan: plans.PlanView; step: plans.PlanStepView; editable: boolean }) {
  const draft = usePlansStore((s) => s.draft);
  const editStep = usePlansStore((s) => s.editStep);
  const editInput = usePlansStore((s) => s.editInput);
  const removeStep = usePlansStore((s) => s.removeStep);
  const markHumanDone = usePlansStore((s) => s.markHumanDone);
  const busy = usePlansStore((s) => s.busy['mark-done'] ?? false);
  const draftStep = draft?.steps.find((s) => s.key === step.key);
  const shown = editable && draftStep ? { ...step, ...draftStep } : step;
  const run = step.run;
  const help = run === null ? [] : browserHelp(run);
  const raw = (shown.channel === 'api' && shown.api !== undefined) || (run !== null && run.status !== 'pending' && (run.request !== undefined || run.response !== undefined || run.prior !== undefined));

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-xl border border-border bg-card p-4">
      <div>
        <div className="flex items-center justify-between gap-2">
          {run !== null && run.channel !== shown.channel ? <MovedBadge from={shown.channel} to={run.channel} /> : <ChannelBadge channel={shown.channel} />}
          {editable && (
            <button onClick={() => removeStep(step.key)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400" title="Remove this step">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
        <h3 className="mt-2 text-base font-semibold text-foreground">{shown.title}</h3>
        {shown.description && <Prose className="mt-1">{shown.description}</Prose>}
        {shown.depends_on.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">Waits for {shown.depends_on.map((key) => plan.steps.find((s) => s.key === key)?.title ?? key).join('; ')}.</p>
        )}
      </div>

      {(run?.verification || help.length > 0 || shown.revert || step.missing_scopes.length > 0) && (
        <ul className="space-y-1 text-xs">
          {step.missing_scopes.length > 0 && <li className="text-amber-400">Needs {step.missing_scopes.length === 1 ? 'a scope' : `${step.missing_scopes.length} scopes`} your Zoom app lacks: {step.missing_scopes.join(', ')}. ZAC adds {step.missing_scopes.length === 1 ? 'it' : 'them'} before the plan runs.</li>}
          {run?.verification && <li className={run.verification.passed || run.verification.skipped ? 'text-emerald-400' : 'text-red-400'}>{checkedLine(run.verification)}</li>}
          {help.map((line) => <li key={line} className="text-sky-300">{line}</li>)}
          {shown.revert && <li className="text-muted-foreground">Undo: {revertLine(shown.revert)}</li>}
        </ul>
      )}

      {shown.inputs.length > 0 && (
        <Section title="Inputs">
          <div className="space-y-2">
            {shown.inputs.map((input) => {
              const value = input.value === null || input.value === undefined ? '' : String(input.value);
              const listId = `${step.key}-${input.name}-candidates`;
              return (
                <div key={input.name}>
                  <label className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{input.label}{input.required && <span className="text-amber-400"> *</span>}</span>
                  </label>
                  <input
                    value={value}
                    disabled={!editable}
                    list={input.candidates && input.candidates.length > 0 ? listId : undefined}
                    onChange={(e) => editInput(step.key, input.name, e.target.value)}
                    placeholder={input.required ? 'required' : 'optional'}
                    className={cn('mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-sm text-foreground', input.required && value === '' ? 'border-amber-500/60' : 'border-border')}
                  />
                  {input.candidates && input.candidates.length > 0 && (
                    <datalist id={listId}>{input.candidates.map((c) => <option key={c} value={c} />)}</datalist>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {shown.channel === 'browser' && shown.browser && (
        <Section title="In your browser">
          <p className="text-xs text-foreground">The recipe for <span className="font-mono">{shown.browser.setting_id}</span>, driven in your browser; its own checks verify it.</p>
        </Section>
      )}

      {shown.channel === 'browser' && shown.agent && (
        <Section title="ZAC in your browser">
          <p className="text-xs text-muted-foreground">The browser agent does this in Zoom's web portal, in the browser panel, where you can watch or take over. It changes only this step's object and never buys anything.</p>
          <p className="whitespace-pre-wrap text-sm text-foreground">{shown.agent.task.text}</p>
          {run?.status === 'waiting_human' && (
            <>
              <p className="text-xs text-orange-300">{String(run.response?.['handed_over'] ?? 'The browser agent handed this step to you.')}</p>
              {shown.human && <p className="whitespace-pre-wrap text-sm text-foreground">{shown.human.instructions}</p>}
              <MarkDone onClick={() => void markHumanDone(plan.id, step.key)} busy={busy} />
            </>
          )}
          {run?.status === 'completed' && typeof run.output?.['summary'] === 'string' && <p className="text-xs text-emerald-400">{String(run.output['summary'])}</p>}
        </Section>
      )}

      {shown.channel === 'human' && shown.human && (
        <Section title="By you">
          <p className="whitespace-pre-wrap text-sm text-foreground">{shown.human.instructions}</p>
          <div className="flex flex-wrap items-center gap-2">
            {shown.human.settings_map_entry_id && <TakeMeThere plan={plan} step={step} entryId={shown.human.settings_map_entry_id} />}
            {run?.status === 'waiting_human' && <MarkDone onClick={() => void markHumanDone(plan.id, step.key)} busy={busy} />}
          </div>
        </Section>
      )}

      {shown.channel === 'api' && run?.status === 'waiting_human' && (
        <Section title="Handed to you">
          <p className="text-xs text-orange-300">{String(run.response?.['handed_over'] ?? 'The API could not do this step.')}</p>
          <p className="whitespace-pre-wrap text-sm text-foreground">{shown.human?.instructions ?? `Do it in the Zoom web portal: ${shown.title}.`}</p>
          <MarkDone onClick={() => void markHumanDone(plan.id, step.key)} busy={busy} />
        </Section>
      )}

      {run && run.status !== 'pending' && (
        <div className="space-y-1 text-xs">
          <p className="font-medium text-foreground">{STEP_RUN_STATUS_TEXT[run.status]}</p>
          {run.error && <p className="text-red-400">{run.error.message}</p>}
          {run.human_done_by && <p className="text-muted-foreground">Marked done by {run.human_done_by === plan.created_by.id ? plan.created_by.email : run.human_done_by}.</p>}
          {run.revert_status && (
            <p className={cn(run.revert_status === 'failed' ? 'text-red-400' : 'text-orange-300')}>
              {REVERT_STATUS_TEXT[run.revert_status]}
              {run.revert_status === 'failed' && typeof run.revert_result?.error === 'string' && <> — {run.revert_result.error}</>}
              {run.revert_status === 'manual' && typeof run.revert_action?.instructions === 'string' && <>: {run.revert_action.instructions}</>}
            </p>
          )}
        </div>
      )}

      {raw && (
        <details className="group rounded-lg border border-border/60 px-3 py-2">
          <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground">Details: the call and what Zoom answered</summary>
          <div className="mt-2 space-y-2">
            {shown.channel === 'api' && shown.api && (
              <>
                <div className="font-mono text-xs text-foreground"><span className="font-semibold">{shown.api.method}</span> {shown.api.path}</div>
                {editable ? <BodyEditor body={shown.api.body} onChange={(body) => editStep(step.key, { api: { ...shown.api!, body } })} /> : shown.api.body && <Json value={shown.api.body} />}
                {shown.verify && <p className="text-xs text-muted-foreground">Checked by GET {shown.verify.path}{shown.verify.checks.length > 0 && <> ({shown.verify.checks.map((c) => c.field).join(', ')})</>}.</p>}
              </>
            )}
            {run?.request && <><p className="text-xs text-muted-foreground">Request</p><Json value={run.request} /></>}
            {run?.response && <><p className="text-xs text-muted-foreground">Response</p><Json value={run.response} /></>}
            {run?.prior && <><p className="text-xs text-muted-foreground">Before the change (what a revert puts back)</p><Json value={run.prior} /></>}
          </div>
        </details>
      )}
    </div>
  );
}

/** The person's word that they did a step that waits for them. */
function MarkDone({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button onClick={onClick} disabled={busy} className="inline-flex w-fit items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Mark done
    </button>
  );
}
